import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as utils from 'globals/utils';
import Radium from 'radium';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import * as keyboardEventHelpers from 'globals/keyboardEvents';
import { loadCookie } from 'globals/cookieHelper';

import InputExpanding from 'shared/components/InputExpanding';
import NewConversationButton from '../../history/NewConversationButton';
import { handleSendSessionMessageOptimistically, handleSendNewMessage } from './actions';
import * as helpers from './helpers';
import * as chatAppSelectors from '../../app/ChatApp/selectors';
import * as routingActions from '../../app/ChatApp/actions_routing';
import config from '../../../../../server/config';
import * as appActions from '../../app/ChatApp/actions';

import messages from './messages';
import styles from './styles';

const ACCEPTED_FILE_TYPES = 'image/*,application/pdf';
const MAX_FILE_UPLOAD_MB = 10000000; // 10mb

export class ChatConversationInput extends Component {
  constructor(props) {
    super(props);

    this.debouncedTypingActivity = debounce(() => helpers.sendChatTypingActivity('typing'), 1000, {
      leading: true,
      maxWait: 2500,
    });

    this.debouncedEndTypingActivity = debounce(() => helpers.sendChatTypingActivity('typing-ended'), 5000);

    this._input = {
      focus: () => {},
      blur: () => {},
    };

    this.state = {
      body: '',
      error: '',
    };
  }

  componentDidMount() {
    if (this._input && !utils.isMobile()) {
      this._input.focus();
    }
  }

  handleKeyPress = e => {
    if (keyboardEventHelpers.isEnterKey(e)) {
      e.preventDefault();
      e.stopPropagation();
      this.handleSubmit();
    } else {
      this.debouncedTypingActivity();
      this.debouncedEndTypingActivity();
    }
  };

  handleSubmit = () => {
    const { handleSendMessage, sessionId, intl } = this.props;
    const body = this.state.body || '';
    const cleanBody = utils.cleanBody(body);

    if (!cleanBody) return;

    handleSendMessage(
      {
        body: cleanBody,
        url: `${config.host}/img/test-avatar.png`,
        user: 'customer@kustomer.com',
        sentBy: {
          type: 'customer',
        },
      },
      sessionId,
      intl,
    );

    this.setState({ body: '' });

    if (utils.isMobile()) {
      this._input.blur();
    } else {
      this._input.focus();
    }
  };

  handleChange = e => {
    this.setState({
      body: e.target.value,
    });
  };

  handleNewConversationClick = () => {
    this.props.redirectToNewConversation();
  };

  handleFileChange = e => {
    const { createAttachment, intl } = this.props;

    const file = get(e, ['target', 'files', [0]]);
    const contentType = get(file, 'type', '');
    const contentLength = get(file, 'size', MAX_FILE_UPLOAD_MB);

    if (!file) return;

    if (contentLength >= MAX_FILE_UPLOAD_MB) {
      const error = intl.formatMessage(messages.upload_fail_fileSize);
      this.setState({ error });
      return;
    }

    if (!contentType.includes('image') && !contentType.includes('pdf')) {
      const error = intl.formatMessage(messages.upload_fail_fileSize);
      this.setState({ error });
      return;
    }

    if (this.state.error) this.setState({ error: '' });

    const messageBody = intl.formatMessage(messages.message_body_attachment);
    const failedMessageBody = intl.formatMessage(messages.message_body_attachment_failed);

    createAttachment({ name: file.name, contentType, contentLength, file, messageBody, failedMessageBody });
  };

  renderDisabled = () => {
    return (
      <div style={styles.wrapperDisabled} className="kustomer-app-reply-input-disabled-text">
        <span style={styles.disabledText}>
          <FormattedMessage {...messages.input_disabled} />
        </span>
      </div>
    );
  };

  renderAttachmentIcon = () => {
    const { sessionId, intl } = this.props;

    if (sessionId === 'new') return null;

    return (
      <label tabIndex="0" htmlFor="fileUpload" style={styles.uploadBtn}>
        <div
          className="uploadIcon kustomer-app-reply-input-upload-attachment-button"
          style={styles.uploadIcon}
          aria-label={intl.formatMessage(messages.aria_label_upload_attachment_button)}
          role="button"
        />
        <input
          id="fileUpload"
          type="file"
          accept={ACCEPTED_FILE_TYPES}
          style={styles.fileInput}
          onChange={this.handleFileChange}
        />
      </label>
    );
  };

  renderInput = () => {
    const { isAfterHours, intl } = this.props;
    const { error } = this.state;

    return (
      <div
        style={[styles.wrapper, error ? styles.hasError : null]}
        className="kustomer-app-reply-input-container"
        onKeyPress={this.handleKeyPress}
      >
        <InputExpanding
          ref={c => (this._input = c)}
          placeholder={
            error ||
            (isAfterHours
              ? intl.formatMessage(messages.placeholder_afterHours)
              : intl.formatMessage(messages.placeholder_inHours))
          }
          value={this.state.body}
          onChange={this.handleChange}
          tabIndex="0"
          error={error}
        />
        {this.renderAttachmentIcon()}
        <div
          style={styles.enter}
          className="enterIcon kustomer-app-reply-input-enter-button"
          onClick={this.handleSubmit}
          onKeyPress={this.handleKeyPress}
          tabIndex="0"
          role="button"
          aria-label={intl.formatMessage(messages.aria_label_send_messages)}
        >
          <div style={styles.enterIcon} />
        </div>
      </div>
    );
  };

  renderLocked = () => {
    const { hasActiveSingleChat, hasMultipleActiveChats } = this.props;
    return (
      <NewConversationButton
        onClick={this.handleNewConversationClick}
        hasActiveSingleChat={hasActiveSingleChat}
        hasMultipleActiveChats={hasMultipleActiveChats}
      />
    );
  };

  render() {
    const { inputDisabled, isLocked } = this.props;
    if (isLocked) {
      return this.renderLocked();
    }
    return inputDisabled ? this.renderDisabled() : this.renderInput();
  }
}

export const mapStateToProps = state => {
  const sessionId = chatAppSelectors.getActiveSessionId(state);
  const isAfterHours = chatAppSelectors.selectIsAfterHours(state);
  const inputDisabled = !!loadCookie(`input-disabled-${sessionId}`);
  const closableChatsEnabled = chatAppSelectors.getCompanyClosableChatEnabled(state);
  const singleSessionChatEnabled = chatAppSelectors.getSingleChatEnabled(state);

  return {
    hasActiveSingleChat: !!chatAppSelectors.getSingleChatId(state),
    hasMultipleActiveChats: singleSessionChatEnabled && chatAppSelectors.hasMoreThanOneActiveSession(state),
    sessionId,
    inputDisabled,
    isAfterHours,
    isLocked: closableChatsEnabled && chatAppSelectors.isSessionLocked(state, sessionId),
  };
};

export const mapDispatchToProps = dispatch => ({
  handleSendMessage(data, sessionId, intl) {
    if (sessionId && sessionId !== 'new') {
      dispatch(handleSendSessionMessageOptimistically(sessionId, data));
    } else {
      dispatch(handleSendNewMessage(data, intl));
    }
  },
  redirectToNewConversation() {
    dispatch(routingActions.handleCreateNewConversation());
    dispatch(appActions.clearSessionQueueEntities());
  },

  createAttachment({ name, contentType, contentLength, file, messageBody, failedMessage }) {
    if (!name && !contentType && !contentLength) return;

    const data = {
      name,
      contentType,
      contentLength,
    };

    // eslint-disable-next-line max-params
    dispatch(appActions.createAttachment(data, file, messageBody, failedMessage));
  },
});

ChatConversationInput.propTypes = {
  handleSendMessage: React.PropTypes.func,
  sessionId: React.PropTypes.string,
  inputDisabled: React.PropTypes.bool,
  isAfterHours: React.PropTypes.bool,
  isLocked: React.PropTypes.bool,
  hasActiveSingleChat: React.PropTypes.bool,
  hasMultipleActiveChats: React.PropTypes.bool,
  redirectToNewConversation: React.PropTypes.func,
  createAttachment: React.PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

ChatConversationInput.defaultProps = {
  sessionId: null,
  isLocked: false,
  hasActiveSingleChat: false,
  hasMultipleActiveChats: false,
  handleSendMessage: () => {},
  redirectToNewConversation: () => {},
};

export const intlComponent = injectIntl(Radium(ChatConversationInput), { withRef: true });

export default connect(mapStateToProps, mapDispatchToProps)(intlComponent);
